import React, {useEffect, useRef, useState} from 'react';
import {Button, Grid, Typography, makeStyles} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import {updateHubspotProsperDealDecisionReferredToUW} from '../deal';
import {ContactFormStore} from '../../ContactForm/ContactFormStore/ContactFormStore';
import {AgenuimContextType} from '../../../Core/agenium/ageniumContext';
import {useInstantApp} from './common/useInstantApp';
import LoadingSpinner from '../../../UI/LoadingSpinner';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {getAgeniumFieldsForDeal} from './utils';

interface DecisionManualUnderwritingProps {
    onExit: () => void;
    onNext: () => void;
    onBackToClients: () => void;
    contactFormStore: ContactFormStore;
    parameterStore: ParameterStore;
    prevModal: string | null | undefined;
    ageniumContext: AgenuimContextType;
}
const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#3f51b5',
        marginBottom: '42px',
    },
    detailsWrap: {
        padding: '10px',
    },
    text: {
        fontFamily: 'LoraSemiBold',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    smallText: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '25.6px',
        color: '#838383',
        marginTop: '18px',
        marginBottom: '18px',
    },
    navigation: {
        marginTop: '20px',
    },
    backBtn: {
        borderRadius: '20px',
    },
    nextBtn: {
        minWidth: '400px',
        borderRadius: '20px',
    },
    progress: {
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        rowGap: '10px',
        justifyContent: 'center',
    },
    redColor: {color: '#f65858'},
}));

const DecisionManualUnderwriting: React.FC<DecisionManualUnderwritingProps> = ({
    onExit,
    onBackToClients,
    onNext,
    contactFormStore,
    parameterStore,
    prevModal,
    ageniumContext,
}) => {
    const {decline, approve, cancelled} = useInstantApp();
    const savedForm = useRef(false);
    const [isLoading, setIsLoading] = useState(!prevModal);
    const [showUnderwritingMessage, setShowUnderwritingMessage] = useState(false);
    const [referReason, setReferReason] = useState('');
    const classes = useStyles();
    const {approval} = parameterStore;

    useEffect(() => {
        const before = async () => {
            if (ageniumContext.applicationData) {
                const decision = ageniumContext.applicationData.find((t) => t.key === 'Case_UWDecision_Overall');
                if (!!decision) {
                    if (decision?.value?.toLowerCase()?.match(/postpone|decline(d?)/gi)) {
                        decline();
                        return;
                    }
                    if (decision?.value?.toLowerCase()?.match(/withdrawn|ineligible|incomplete/gi)) {
                        cancelled();
                        return;
                    }
                    if (!decision?.value?.match(/(?<!p)refer/gi)) {
                        approve();
                        return;
                    }
                }
                setShowUnderwritingMessage(true);
            }
            // Always set loading to false after processing, whether or not applicationData exists
            setIsLoading(false);

            if (!savedForm.current) {
                savedForm.current = true;
                ageniumContext
                    .updateApplicationData(
                        {
                            ageniumFields: getAgeniumFieldsForDeal(ageniumContext.applicationData),
                        },
                        parameterStore.formid,
                    )
                    .finally(() => {
                        updateHubspotProsperDealDecisionReferredToUW().finally(() => contactFormStore.saveForm());
                    });
            }
        };
        setTimeout(before, !prevModal ? 3000 : 0);
        setReferReason(
            ageniumContext.applicationData?.find((t) => t.key === 'Case_UWDecision_Refer_Notes')?.value || '',
        );
    }, [ageniumContext.applicationData]);

    useEffect(() => {
        ageniumContext.initSdk();
    }, []);

    return (
        <>
            <Grid container classes={{root: classes.wrapper}}>
                <Typography classes={{root: classes.title}} data-testId="decisionManualUnderwriting">
                    Your client Requires manual underwriting.
                </Typography>
                <Grid container classes={{root: classes.detailsWrap}} spacing={4}>
                    <Grid item xs={9}>
                        <Typography classes={{root: classes.smallText}}>{referReason}</Typography>
                        <Typography classes={{root: classes.text}}>What happens Next?</Typography>
                        <Typography classes={{root: classes.smallText}}>
                            Our underwriters will need to review the application and may need additional information to
                            finalize the app. We’ll make sure to email/text you in the next few days with any additional
                            requirements.
                        </Typography>
                    </Grid>
                    {showUnderwritingMessage && (
                        <Grid item xs={9}>
                            <Typography className={`${classes.text} ${classes.redColor}`}>
                                Back to manual underwriting!
                            </Typography>
                            <Typography className={`${classes.smallText} ${classes.redColor}`}>
                                It looks like this case still requires manual underwriting. If you believe this is an
                                error, please contact @eng-oncall via slack.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={2} justify="space-around" className={classes.navigation}>
                    {approval.conditionalCoverageElected.value ? (
                        <>
                            <Grid item>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={onExit}
                                    className={classes.nextBtn}
                                >
                                    Exit
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={onBackToClients}
                                    className={classes.nextBtn}
                                >
                                    Back to clients
                                </Button>
                            </Grid>
                        </>
                    ) : (
                        <Grid item>
                            <Button color="primary" variant="contained" onClick={onNext} className={classes.nextBtn}>
                                Next
                            </Button>
                        </Grid>
                    )}
                </Grid>
                <code style={{color: '#00f', fontSize: '12px', marginTop: '100px', width: '100%'}}>
                    <div>
                        <b style={{color: '#f50'}}>Application Id:</b> {ageniumContext.applicationId}{' '}
                    </div>
                    <div>
                        <b style={{color: '#f50'}}>Application Number:</b> {ageniumContext.applicationNumber}
                    </div>
                    <div>
                        <b style={{color: '#f50'}}>Policy Number:</b> {ageniumContext.policyNumber}
                    </div>
                </code>
            </Grid>
            {isLoading && (
                <LoadingSpinner
                    title={'Retrieving Underwriting Decision'}
                    message={'This may take up to 30 seconds.'}
                />
            )}
        </>
    );
};

export default DecisionManualUnderwriting;
